import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { KeyValuePair, ConnectionPointType, Application, ComponentDefinition, ConnectionPoint, Workspace } from '../../models/index';
import { IApplicationService, IConnectionPointService, IUserService, ILoggerService, IStoreService } from '../../services/index';
import { isWideDb, guidEmpty, applicationCreated, connectionPointTypes } from '../../common';
import { Store, StoreElement } from '../../stateManagement';

class ImportApplicationWizardController implements angular.IComponentController {
    application: any;
    applicationList: Application[];
    appname: string;
    connectionPointTypes: KeyValuePair<ConnectionPointType, string>[];
    connectionPoints: ConnectionPoint[];
    daysOfWeek: KeyValuePair<number, string>[];
    fileList: FileList;
    filepath: string;
    importMode: string;
    invalidCP: boolean;
    isLoading: boolean;
    progressPercentage: number;
    saving: boolean;
    selectedApplication;
    selectedUser: string;
    step: number;
    unusedUsers: any[];
    updating: boolean;
    users: any[];
    wideConnectionPoints: any[];

    private _extractFolder: string;
    private _modal: any; //JQuery
    private _garbages: (() => void)[] = [];
    private _userName: string;


    static $inject = [
        '$rootScope',
        '$timeout',
        'applicationService',
        'connectionPointService',
        'userService',
        'loggerService',
        'storeService'
    ];

    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _applicationService: IApplicationService,
        private _connectionPointService: IConnectionPointService,
        private _userService: IUserService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService
    ) {
    }

    $onInit() {
        this.step = 1;
        this.importMode = 'Create';
        this.progressPercentage = 0;
        this.connectionPointTypes = connectionPointTypes
            .filter(t => t.key !== ConnectionPointType.PI && t.key !== ConnectionPointType.Excel)
            .sort((a, b) => a.value > b.value ? 1 : -1);
        this._modal = angular.element('#importApplicationModal');

        this._storeService.register(
            store => this.refreshStore(store),
            [StoreElement.Authentication]
        );

        this.daysOfWeek = moment.weekdays().map((d, i) => {
            return { key: i, value: d };
        });

        this._modal.on('hidden.bs.modal', event => {
            this.application = null;
            this.step = 1;
            this.progressPercentage = 0;
        });

        this._userService
            .getAll()
            .then(data => this.users = data);
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    nextStep() {
        if (this.importMode === 'Create') {
            this.createNextStep();
        } else {
            this.updateNextStep();
        }
    }

    private updateNextStep() {
        const workspace: Workspace = {
            application: this.selectedApplication?.name,
            environment: 'Development',
            model: 'Model'
        };
        switch (this.step) {
            case 1:
                this.upload(this.fileList);
                break;
            case 2:
                //TODO get existing application connection points and filter
                this._storeService.dispatchers().authentication
                    .connect(
                        this._userName,
                        workspace,
                        this.selectedApplication.guid,
                        false
                    )
                    .then(() => {
                        this._connectionPointService.getAll()
                            .then(existing => {
                                this.connectionPoints = this.connectionPoints
                                    .filter(c => _.every(existing, e => e.connectionPoint.guid !== c.guid));
                                this.wideConnectionPoints = this.connectionPoints
                                    .filter(cp => isWideDb(cp));
                                this.computeValidCP();
                                this.step++;
                            });
                    });
                break;
            case 3:
                break;
        }
    }

    private createNextStep() {
        const currentUser = this._userName.toLowerCase();
        switch (this.step) {
            case 1:
                this.upload(this.fileList);
                break;
            case 2:
                this.step++;
                this.computeValidCP();
                break;
            case 3:
                if (!_.some(this.application.allowedUserCollection,
                    (su: string) => su.toLowerCase() === currentUser)) {
                    this.addUser(this._userName);
                }

                this.unusedUsers = this.users.map(d => d)
                    .filter(u => this.application.allowedUserCollection.indexOf(u) < 0);
                if (!this.selectedUser) {
                    this.selectedUser = this.unusedUsers[0];
                }
                this.step++;
                break;
            case 4:
                this.step++;
                if (this.wideConnectionPoints && this.wideConnectionPoints.length > 0 && this.application.defaultConnectionPointGuid === guidEmpty) {
                    this.application.defaultConnectionPointGuid = this.wideConnectionPoints[0].guid;
                }
                break;
            default:
                break;
        }
    }

    prevStep() {
        this.step--;
    }

    select(files: FileList) {
        this.fileList = files;
    }


    selectApplication(application) {
        this.selectedApplication = application;
    }

    isSelected(guid) {
        const result = this.selectedApplication &&
            guid === this.selectedApplication.guid;
        return result;
    }

    isValid(applicationName) {
        const isValid = applicationName && _.every(this.applicationList, a => a.name.toLocaleLowerCase() !== applicationName.toLocaleLowerCase());
        return isValid;
    }

    test(cp) {
        cp.isTesting = true;
        this._connectionPointService.testNew(cp, cp.newPassword)
            .then(data => cp.Ok = data.isAvailable)
            .catch(reason => cp.Ok = false)
            .finally(() => cp.isTesting = false);
    }

    status(connectionPoint) {
        let result = {};
        if (connectionPoint.Ok === true) {
            result = { 'success': true };
        } else if (connectionPoint.Ok === false) {
            result = { 'error': true };
        }
        return result;
    }

    upload(files: FileList) {
        this.updating = true;
        this.fileList = files;
        if (files && files.length) {
            this._applicationService
                .upload(files[0], (evt => {
                    this.progressPercentage = 100.0 * evt.loaded / evt.total;
                }))
                .then(data => {
                    this._loggerService.log(`file ${data.config.file.name} uploaded. Response: ${data.data}`, false);
                    this.application = data.data.application;
                    delete this.application['guid'];
                    this.application.name = data.data.name;
                    this.application.allowedUserCollection = [];
                    data.data.connectionPoints.forEach(cp => {
                        cp.oldPassword = cp.password;
                        cp.newPassword = cp.password;
                    });
                    this.connectionPoints = data.data.connectionPoints;
                    this.wideConnectionPoints = this.connectionPoints.filter(cp => isWideDb(cp));
                    this._extractFolder = data.data.extractFolder;
                    if (this.importMode === 'Update') {
                        let application = _.find(this.applicationList, a => a.guid === this.application.guid);
                        if (!application) {
                            application = _.find(this.applicationList, a => a.name === this.application.name);
                        }
                        this.selectedApplication = application;
                        this._$timeout(() => {
                            $(`#row-${application?.guid}`)[0]?.scrollIntoView(false);
                        });

                    }
                    this.step++;
                    this.fileList = null;
                })
                .catch((reason) => {
                    this._loggerService.logError(reason.data.Message);
                })
                .finally(() => {
                    this.updating = false;
                });
        }
    }

    addUser(user) {
        this.application.allowedUserCollection.push(user);
        this.unusedUsers = this.users.filter(u => this.application.allowedUserCollection.indexOf(u) < 0);
        this.selectedUser = this.unusedUsers[0];
    }

    removeUser(user) {
        _.remove(this.application.allowedUserCollection, u => u === user);
        this.unusedUsers = this.users.filter(u => this.application.allowedUserCollection.indexOf(u) < 0);
    }

    save() {
        const postObject = {
            application: this.application,
            connectionPoints: this.connectionPoints,
            extractFolder: this._extractFolder,
            userList: this.application.allowedUserCollection
        };
        this.saving = true;
        this._applicationService.postImport(postObject)
            .then(data => {
                this._modal.modal('hide');
                this._$rootScope.$broadcast(applicationCreated);
                this._storeService.dispatchers().authentication.disconnect();
            })
            .catch(reason => {
                this._loggerService.logError(reason);
            })
            .finally(() => {
                this.saving = false;
            });
    }

    update() {
        //#33050 quick V2 fix for missing doc & comments update
        this.selectedApplication.shortComment = this.application.shortComment;
        this.selectedApplication.longComment = this.application.longComment;
        this.selectedApplication.documentation = this.application.documentation;
        const postObject = {
            application: this.selectedApplication,
            connectionPoints: this.connectionPoints,
            extractFolder: this._extractFolder,
            userList: []
        };
        this.updating = true;
        this._applicationService.updateFromFile(postObject)
            .then(data => {
                this._modal.modal('hide');
                this._storeService.dispatchers().authentication.disconnect();
            })
            .finally(() => {
                this.updating = false;
            });
    }

    computeValidCP() {
        this.invalidCP = _.some(this.connectionPoints, cp => cp.type === ConnectionPointType.PI || cp.type === ConnectionPointType.Excel);
    }

    private refreshStore(store: Store) {
        this._userName = store.authentication.value.userName;
    }
}

export const wtImportApplicationWizard: ComponentDefinition = {
    componentId: 'wtImportApplicationWizard',
    controller: ImportApplicationWizardController,
    template: require('./importApplicationWizard.component.html'),
    controllerAs: 'vm',
    bindings: {
        applicationList: '<'
    }
};